import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import CircularInfinite from 'fe-design-base/molecules/Loaders/CircularInfinite';

import { getCurrentLocationUuid } from 'selectors/session';

import { DETAIL_VIEWS, VIEWS } from 'features/notifications/componentRegistry';
import { useRequestsFeed } from 'features/notifications/hooks/useRequestsFeed';
import {
  clearInvalidation,
  clearSelectedRequest,
  markTabViewedAndFetchCounts,
  selectInvalidatedTabs,
  selectSelectedRequest,
  setSelectedRequest,
} from 'features/notifications/slice';
import { RequestGroup, RequestType } from 'features/notifications/types';

import { EmptyState } from './EmptyState';

type DetailViewKey = keyof typeof DETAIL_VIEWS;
type ViewKey = keyof typeof VIEWS;

const MyRequests = () => {
  const dispatch = useDispatch();
  const currentLocation = useSelector(getCurrentLocationUuid);
  const selectedRequest = useSelector(selectSelectedRequest);
  const invalidatedTabs = useSelector(selectInvalidatedTabs);
  const prevSelectedRequestRef = useRef(selectedRequest);

  const {
    requests,
    loading,
    isFetchingMore,
    hasMore,
    sentinelRef,
    fetchInitialRequests,
  } = useRequestsFeed({
    tab: 'my',
    locationId: currentLocation,
  });

  const handleRequestClick = (request: RequestGroup, type: RequestType) => {
    dispatch(setSelectedRequest({ request, type }));
  };
  const handleClose = () => {
    dispatch(clearSelectedRequest());
  };

  useEffect(() => {
    dispatch(
      markTabViewedAndFetchCounts({ tab: 'my', locationId: currentLocation })
    );
  }, [dispatch, currentLocation]);

  useEffect(() => {
    const wasViewingDetail = prevSelectedRequestRef.current?.request;
    const isNowViewingList = !selectedRequest?.request;
    const shouldRefetch = wasViewingDetail && isNowViewingList;
    const isInvalidated = invalidatedTabs.my;

    prevSelectedRequestRef.current = selectedRequest;

    if (loading || shouldRefetch || isInvalidated) {
      fetchInitialRequests();
      if (isInvalidated) {
        dispatch(clearInvalidation('my'));
      }
    }
  }, [
    currentLocation,
    dispatch,
    selectedRequest,
    loading,
    invalidatedTabs,
    fetchInitialRequests,
  ]);

  if (loading) {
    return (
      <Box hcenter vcenter h="50vh">
        <CircularInfinite size="large" variant="dark" />
      </Box>
    );
  }

  if (requests.length === 0) return <EmptyState />;

  if (selectedRequest && selectedRequest.request) {
    const detailViewKey = selectedRequest.type as DetailViewKey;
    return (
      <Box h="100%">
        {DETAIL_VIEWS[detailViewKey](selectedRequest.request.id, handleClose)}
      </Box>
    );
  }

  return (
    <Box scrollY>
      {requests.map(request => {
        const viewKey = request.type as ViewKey;
        const ViewComponent = VIEWS[viewKey];
        return (
          <Box key={request.id}>
            {/* TypeScript cannot infer union type narrowing with dynamic keys */}
            {/* @ts-expect-error - request.type guarantees correct parameter type */}
            {ViewComponent(request, handleRequestClick)}
          </Box>
        );
      })}
      {hasMore && <Box ref={sentinelRef} h={1} w="100%" />}
      {isFetchingMore && (
        <Box hcenter vcenter py={32}>
          <CircularInfinite size="medium" variant="dark" />
        </Box>
      )}
    </Box>
  );
};

export default MyRequests;
